import type {
  IDrainedStreamChunks,
  IDrainStreamChunksProps,
  IEnqueueStreamChunkProps,
  IHasPendingStreamChunksProps,
  IPendingMessageStreamChunk,
  TPendingStreamChunkBuffer,
} from "../types/chat";

// NOTE: NSAI-5908 — the platform-agnostic half of stream coalescing. The
// server emits one socket frame per model token; applying each frame to the
// cache separately re-renders the transcript once per token, whatever the
// token rate. Frames are parked here instead and the platform drains them on
// its own cadence (web: one animation frame while visible, about once a
// second while hidden). Concatenation preserves arrival order, so a drained
// chunk is exactly what the frames would have appended one by one. A full
// replacement (`messageContentUpdate`) is kept as such: it supersedes the
// deltas parked before it, and only the deltas after it are appended on top.

export const createPendingStreamChunkBuffer = (): TPendingStreamChunkBuffer =>
  new Map();

export const enqueueStreamChunk = ({
  buffer,
  chatUuid,
  uuid,
  delta,
  reasoning,
  replacementContent,
}: IEnqueueStreamChunkProps): void => {
  let chatChunks = buffer.get(chatUuid);
  if (!chatChunks) {
    chatChunks = new Map();
    buffer.set(chatUuid, chatChunks);
  }

  const chunk = chatChunks.get(uuid) ?? { content: "", reasoning: "" };
  const nextChunk: IPendingMessageStreamChunk = {
    content: chunk.content,
    reasoning: chunk.reasoning + (reasoning ?? ""),
  };
  if (chunk.replacementContent !== undefined) {
    nextChunk.replacementContent = chunk.replacementContent;
  }
  // NOTE: A replacement overwrites the text the earlier deltas had appended,
  // exactly as applying the frames one by one would have — so those deltas
  // are dropped and only the ones that follow the replacement are kept.
  if (replacementContent !== undefined) {
    nextChunk.replacementContent = replacementContent;
    nextChunk.content = "";
  }
  nextChunk.content += delta ?? "";
  chatChunks.set(uuid, nextChunk);
};

// NOTE: Removes and returns what is pending — for one chat, or for every chat
// when `chatUuid` is omitted. Chats are returned in insertion order, messages
// within a chat likewise, so a drain applies frames in the order they arrived.
export const drainStreamChunks = ({
  buffer,
  chatUuid,
}: IDrainStreamChunksProps): IDrainedStreamChunks[] => {
  if (chatUuid !== undefined) {
    const chunks = buffer.get(chatUuid);
    if (!chunks) {
      return [];
    }
    buffer.delete(chatUuid);
    return [{ chatUuid, chunks }];
  }

  const drained: IDrainedStreamChunks[] = [];
  for (const [pendingChatUuid, chunks] of buffer) {
    drained.push({ chatUuid: pendingChatUuid, chunks });
  }
  buffer.clear();
  return drained;
};

export const hasPendingStreamChunks = ({
  buffer,
  chatUuid,
}: IHasPendingStreamChunksProps): boolean => {
  if (chatUuid !== undefined) {
    return (buffer.get(chatUuid)?.size ?? 0) > 0;
  }
  return buffer.size > 0;
};
